import { $UserAddPermissions, ACTIONS } from "@store/user-store";
import { CheckboxCheckedSVG, CheckboxSVG } from "@components/svgs";
import { Checkbox, Table, TableBody, TableCell, TableRow } from "@mui/material";
import { SELECTS_ID } from "@store/company/procedure-instrunctions-store";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ruRU } from "@mui/x-date-pickers/locales";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useCompaniesQuery } from "@lib/company";
import { useEffect, useMemo, useState } from "react";

import { ColorfulButton } from "@components/common/common";
import { DropDownMultiSelect } from "@ui/drop-down";
import SpecialistPlanService from "@services/specialist-plan-service";
import { WorkplanGroup } from "../workplan-group/workplan-group";
import clsx from "clsx";
import moment, { Moment } from "moment";
import styles from "./workplan-table.module.scss";
import { useAsyncConfirm } from "@hooks/useAsyncConfirm";
import { useChoiceState } from "@hooks/useChoiceState";
import useModal from "@modals/modal-hook";
import { useUnit } from "effector-react";
import { UseSortingResult } from "@hooks/useSorting";
import { GetWorkplanTasksFilters, WorkplanGroupT } from "@interfaces/workplan";
import { useQueryClient } from "react-query";
import { workplanQueryKeys } from "@lib/workplan/queryKeys";

type SelectListType = {
  id: number;
  title: string;
};

type TWorkplanTable = {
  groups: WorkplanGroupT[];
  filters: GetWorkplanTasksFilters;
  selectedCompanyId?: number;
  companyId?: number;
  isCompanyDefined?: boolean;
  resetCompany: boolean;
  resetStatus: boolean;
  resetCategory: boolean;
  handlerChoiceCompany: (values: SelectListType[]) => void;
  handlerChoiceCategory: (values: SelectListType[]) => void;
  handlerChoiceStatus: (values: SelectListType[]) => void;
  handlerReset: () => void;
  onStartDateChange: (date: Moment | null) => void;
  onEndDateChange: (date: Moment | null) => void;
  clearAllFilters: () => void;
  handlerArchive: (state: boolean) => void;
  sorting: UseSortingResult;
  withDeleteChoice?: boolean;
};

export const WorkplanTable: React.FC<TWorkplanTable> = ({
  groups,
  filters,
  selectedCompanyId,
  companyId,
  isCompanyDefined = false,
  resetCompany,
  resetStatus,
  resetCategory,
  handlerChoiceCompany,
  handlerChoiceCategory,
  handlerChoiceStatus,
  handlerReset,
  onStartDateChange,
  onEndDateChange,
  clearAllFilters,
  handlerArchive,
  sorting,
  withDeleteChoice = true,
}) => {
  const queryClient = useQueryClient();

  const { open } = useModal();
  const permissions = useUnit($UserAddPermissions);
  const { companies } = useCompaniesQuery();
  const confirm = useAsyncConfirm();

  const [openedGroup, setOpenedGroup] = useState<string>();

  const choice = useChoiceState(groups, (group) => group.id ?? -1);

  const filteredGroups =
    filters.groups.length > 0
      ? groups.filter((group) => filters.groups.includes(group.title))
      : groups;

  // NOTE: this useState is here because i couldn't find a way to abstract it into custom `useEffectOnce` hook
  const [effectRun, setEffectRun] = useState(false);
  useEffect(() => {
    if (effectRun || groups.length === 0) return;

    setOpenedGroup(groups[0].title);
    setEffectRun(true);
  }, [effectRun, groups]);

  const handleOpenGroup = (group: string) => {
    if (openedGroup === group) {
      return setOpenedGroup(undefined);
    }

    setOpenedGroup(group);
  };

  const listStatus = useMemo(() => {
    return [
      { title: "Все статусы", id: SELECTS_ID },
      { title: "Создание", id: 1 },
      { title: "На подписании", id: 2 },
      { title: "Документы подписаны", id: 3 },
      { title: "Подписание проверено", id: 4 },
      { title: "В работе", id: 5 },
      { title: "Выполнено", id: 6 },
    ];
  }, []);

  const listCompanies = useMemo(() => {
    return [
      {
        title: "Все компании",
        id: SELECTS_ID,
      },
      ...(companies?.map((company) => ({
        title: company.name,
        id: company.id,
      })) ?? []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, companies]);

  const handleDeleteGroup = async (
    groupId: number,
    shouldInvalidate = true
  ) => {
    return new Promise((resolve, reject) => {
      if (companyId) {
        SpecialistPlanService.deleteGroup(companyId, groupId, (err, res) => {
          if (err || !res) {
            reject(err);
            return console.error("При удалении категории произошла ошибка");
          }
          resolve(res.data);
          if (res.data === 1 && shouldInvalidate) {
            queryClient.invalidateQueries(workplanQueryKeys.all);
          }
        });
      }
    });
  };

  const handleDeleteSelectedGroups = async () => {
    if (!withDeleteChoice) return;

    const ok = await confirm({
      modalData: {
        text: "Вы уверены, что хотите удалить выбранные категории и все задачи?",
      },
    });
    if (!ok) return;
    const promises = [];
    for (const group of choice.selectedItems) {
      promises.push(handleDeleteGroup(group.id ?? -1, false));
    }
    await Promise.all(promises);
    choice.clearSelectedItems();

    queryClient.invalidateQueries(workplanQueryKeys.all);
  };

  return (
    <div className={styles.container}>
      <Table
        aria-label="collapsible table"
        id="resizable-table"
        stickyHeader
        className={styles.workplanTable}
        style={{
          position: "relative",
          maxWidth: "100vh",
        }}
      >
        <TableBody>
          <TableRow className={styles.headFilter}>
            <TableCell align="left" colSpan={7}>
              <div
                className={clsx(styles.headTable, {
                  [styles.headTable_inCompany]: isCompanyDefined,
                })}
              >
                <div
                  className={clsx(styles.header_filters, {
                    [styles.header_filters__sep]: !companyId,
                  })}
                >
                  <DropDownMultiSelect
                    size={"big"}
                    typeStyle={"input"}
                    defaultTitle={"Все категории"}
                    allId={SELECTS_ID}
                    direction={[
                      { id: SELECTS_ID, title: "Выбрать все" },
                      ...groups.map((group, i) => ({
                        id: i + 1,
                        title: group.title,
                      })),
                    ]}
                    reset={resetCategory}
                    handlerReset={handlerReset}
                    handlerList={handlerChoiceCategory}
                  />
                  {!isCompanyDefined && (
                    <DropDownMultiSelect
                      selectOnly={selectedCompanyId}
                      size={"big"}
                      typeStyle={"input"}
                      defaultTitle={"Все компании"}
                      direction={listCompanies}
                      reset={resetCompany}
                      handlerReset={handlerReset}
                      handlerList={handlerChoiceCompany}
                    />
                  )}
                  <DropDownMultiSelect
                    size={"medium"}
                    typeStyle={"input"}
                    defaultTitle={"Все статусы"}
                    direction={listStatus}
                    reset={resetStatus}
                    handlerReset={handlerReset}
                    handlerList={handlerChoiceStatus}
                  />
                  <LocalizationProvider
                    dateAdapter={AdapterMoment}
                    adapterLocale={"ru"}
                    localeText={
                      ruRU.components.MuiLocalizationProvider.defaultProps
                        .localeText
                    }
                  >
                    <DatePicker
                      className={clsx(styles.pickers_input)}
                      value={moment(filters.startDate)}
                      onChange={onStartDateChange}
                      format="DD.MM.YYYY"
                      autoFocus={false}
                      slotProps={{
                        actionBar: { actions: ["clear", "cancel", "accept"] },
                        textField: {
                          variant: "standard",
                          placeholder: "Начало",
                        },
                      }}
                      localeText={{
                        toolbarTitle: "",
                        cancelButtonLabel: "Отменить",
                        okButtonLabel: "ОК",
                        clearButtonLabel: "Очистить",
                      }}
                    />
                    <DatePicker
                      className={clsx(styles.pickers_input)}
                      value={moment(filters.deadline)}
                      onChange={onEndDateChange}
                      format="DD.MM.YYYY"
                      slotProps={{
                        actionBar: { actions: ["clear", "cancel", "accept"] },
                        textField: {
                          variant: "standard",
                          placeholder: "Окончание",
                        },
                      }}
                      localeText={{
                        toolbarTitle: "",
                        cancelButtonLabel: "Отменить",
                        okButtonLabel: "ОК",
                        clearButtonLabel: "Очистить",
                      }}
                    />
                  </LocalizationProvider>
                  <div className={styles.checkbox}>
                    <Checkbox
                      color="primary"
                      checked={filters.statuses?.includes(7)}
                      onChange={(e) => handlerArchive(e.target.checked)}
                      icon={<CheckboxSVG />}
                      checkedIcon={<CheckboxCheckedSVG />}
                      title="В архиве"
                    />
                    <span>Показать задачи в архиве</span>
                  </div>
                  {isCompanyDefined && (
                    <div className={clsx(styles.plan_top_content)}>
                      {permissions.hasPermission(
                        ACTIONS.companySpecialistWorkPlan_groups_allowedToCreate
                      ) && (
                        <ColorfulButton
                          text="Добавить категорию"
                          onClick={() =>
                            open("AddPlanGroupModal", {
                              btnText: "Добавить",
                              modalData: {
                                modalTitle: "Добавить категорию",
                                companyId: companyId,
                                categories: groups,
                              },
                            })
                          }
                        />
                      )}
                    </div>
                  )}
                  <button className="modal_btn" onClick={clearAllFilters}>
                    Очистить фильтры
                  </button>
                </div>
              </div>
            </TableCell>
          </TableRow>
          {filteredGroups.map((group) => (
            <WorkplanGroup
              key={group.title}
              isOpen={openedGroup === group.title}
              isSelected={choice.getIsItemSelected(group)}
              group={group}
              filters={filters}
              sorting={sorting}
              isCompanyDefined={isCompanyDefined}
              withCheckbox={withDeleteChoice}
              choice={choice}
              onDeleteSelectedGroups={handleDeleteSelectedGroups}
              onToggleOpen={() => handleOpenGroup(group.title)}
              onSelectChange={(_, checked) => choice.selectById(group, checked)}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
